// Behavior-classified zones: a national 27-class scheme, separate from the Mahalanobis
// multi-input relative zones. Class codes, the percentile rescale, and the shared dissolve
// stay, because the editor and the decile path use them. Presets run the decile chain.
import { polygonAreaM2 } from "./samplingDesign";
import { buildZoneGeom, dissolveLabel, type ZoneGeom } from "./zoneDesign";

// ts` and is now EMPIRICAL: measured raw value at each of the 1st–99th nationwide percentiles
// interpolated linearly between them. It replaced a straight-line rescale between two endpoints
// which called itself a percentile but spaced the numbers evenly across a range instead of across
// the distribution.
export {
  toPct,
  toIndexScale,
  PERCENTILE_TABLE,
  setServedPercentiles,
  type BehaviorKey,
} from "./behaviorPercentile";
import { toPct, type BehaviorKey } from "./behaviorPercentile";

const pctOf = (v: number, key: BehaviorKey) => toPct(v, key)!; // module-level default breaks (v is always finite)

export interface BehaviorDim {
  name: string; // dimension name
  chars: [string, string, string]; // single-char category codes: [low, mid, high]
  names: [string, string, string]; // full category names
  breaks: [number, number]; // v < b0 → cat0; b0 ≤ v ≤ b1 → cat1; v > b1 → cat2
}
// The getter costs a few interpolations per read and can never go stale.
const dim = (
  name: string,
  chars: [string, string, string],
  names: [string, string, string],
  key: BehaviorKey,
  lo: number,
  hi: number,
): BehaviorDim => ({
  name,
  chars,
  names,
  get breaks(): [number, number] {
    return [pctOf(lo, key), pctOf(hi, key)];
  },
});
export const MOISTURE: BehaviorDim = dim(
  "Effective Moisture",
  ["D", "M", "W"],
  ["Dry", "Mid", "Wet"],
  "moisture",
  -10,
  20,
);
export const PRODUCTIVITY: BehaviorDim = dim(
  "Productivity",
  ["U", "M", "P"],
  ["Unproductive", "Mid", "Productive"],
  "productivity",
  -20,
  15,
);
export const FERTILITY: BehaviorDim = dim(
  "Fertility",
  ["I", "M", "F"],
  ["Infertile", "Mid", "Fertile"],
  "fertility",
  0.7,
  3.8,
);
export const BEHAVIOR_DIMS: [BehaviorDim, BehaviorDim, BehaviorDim] = [
  MOISTURE,
  PRODUCTIVITY,
  FERTILITY,
];

// category index (0/1/2) for a value on a dimension; null when the value is missing
export function catOf(
  v: number | null | undefined,
  dim: BehaviorDim,
): number | null {
  if (v == null || !Number.isFinite(v)) return null;
  return v < dim.breaks[0] ? 0 : v <= dim.breaks[1] ? 1 : 2;
}
// class index 0..26 from the three category indices (moisture·9 + productivity·3 + fertility); null if any missing
export function classIndex(
  m: number | null,
  p: number | null,
  f: number | null,
): number | null {
  return m == null || p == null || f == null ? null : m * 9 + p * 3 + f;
}
const cats = (idx: number): [number, number, number] => [
  Math.floor(idx / 9),
  Math.floor((idx % 9) / 3),
  idx % 3,
];
export function classCode(idx: number): string {
  const [m, p, f] = cats(idx);
  return MOISTURE.chars[m] + PRODUCTIVITY.chars[p] + FERTILITY.chars[f];
}
export function classLabel(idx: number): string {
  const [m, p, f] = cats(idx);
  return `${MOISTURE.names[m]}, ${PRODUCTIVITY.names[p]}, ${FERTILITY.names[f]}`;
}
// class index from an explicit per-dimension pick (for the "classify uploaded zones" path)
export function classIndexFromCats(m: number, p: number, f: number): number {
  return m * 9 + p * 3 + f;
}
// a stable, well-spread color per class (golden-angle hue) — the code label disambiguates any near hues
export function behaviorColor(classIdx: number): string {
  return `hsl(${(classIdx * 137.508) % 360}, 68%, 52%)`;
}

export interface BehaviorZoneResult {
  fc: GeoJSON.FeatureCollection; // one feature per surviving class: {zone(=classIndex), code, label, area_acres}, MultiPolygon
  zoneCount: number;
  classified: number; // cells with a class (non-missing)
  labelPoints: { zone: number; code: string; lng: number; lat: number }[];
  labels: number[]; // per-cell class index (0..26, or -1 = missing/gap), aligned with the input geoms — for the editor
}

export interface BehaviorDrivers {
  moisture: (number | null)[];
  productivity: (number | null)[];
  fertility: (number | null)[];
}

export function rescaleDrivers(raw: BehaviorDrivers): BehaviorDrivers {
  return {
    moisture: raw.moisture.map((v) => toPct(v, "moisture")),
    productivity: raw.productivity.map((v) => toPct(v, "productivity")),
    fertility: raw.fertility.map((v) => toPct(v, "fertility")),
  };
}

// Dissolve a per-cell class-index array (0..26; -1 = gap, skipped) into behavior zones.
// The interactive editor re-dissolves after a cell paint. Same-class cells
// combine into ONE zone — so painting cells to an already-present class code AUTO-MERGES with that zone (this
// is the "same name → merge" behavior for free). Deepest-interior label anchor + per-zone index means. Does
// NOT re-absorb pockets (manual edits are honored as-is). `geom` may be precomputed to skip the O(n) rebuild.
export function behaviorZonesFromLabels(
  geoms: { type: string; coordinates: unknown }[],
  labels: number[],
  drivers: BehaviorDrivers,
  opts?: {
    geom?: ZoneGeom;
    codeOf?: (cls: number) => string;
    labelOf?: (cls: number) => string;
  },
): {
  fc: GeoJSON.FeatureCollection;
  labelPoints: { zone: number; code: string; lng: number; lat: number }[];
} {
  const codeOf = opts?.codeOf ?? classCode;
  const labelOf = opts?.labelOf ?? classLabel;
  const { ringsPerCell, centroids, adj } = opts?.geom ?? buildZoneGeom(geoms);
  // interior degree of THIS grid: 6 for the legacy cell grid, 4 for the 8 m square grid (rook-4) —
  // a cell with fewer neighbors is on the grid boundary. Derived from the adjacency so it's
  // shape-agnostic. A hardcoded `< 6` treats every square cell as boundary and the
  // anchor search collapses.
  const interiorDegree = adj.reduce((m, a) => Math.max(m, a.length), 0);
  const byClass = new Map<number, number[]>();
  labels.forEach((l, i) => {
    if (l < 0) return;
    const arr = byClass.get(l);
    if (arr) arr.push(i);
    else byClass.set(l, [i]);
  });
  const dissolved = [...byClass.entries()].map(([cls, members]) => {
    const geom = dissolveLabel(ringsPerCell, members);
    return { cls, members, geom, area: polygonAreaM2(geom) / 4046.86 };
  });
  dissolved.sort((x, y) => y.area - x.area);

  const meanOf = (arr: (number | null)[], members: number[]): number | null => {
    let s = 0,
      k = 0;
    for (const i of members) {
      const v = arr[i];
      if (v != null && Number.isFinite(v)) {
        s += v;
        k++;
      }
    }
    return k ? Math.round((s / k) * 100) / 100 : null;
  };
  const features: GeoJSON.Feature[] = [];
  const labelPoints: {
    zone: number;
    code: string;
    lng: number;
    lat: number;
  }[] = [];
  for (const { cls, members, geom, area } of dissolved) {
    // label anchor: the DEEPEST-interior cell of the LARGEST contiguous patch — the member whose
    // graph distance from the patch boundary is greatest (a discrete pole of inaccessibility).
    // Unlike a centroid (which can fall OUTSIDE a concave/annular zone) this is always a member
    // cell sitting as far from every edge as possible, so the 3-letter code renders WELL INSIDE the
    // zone with room for the text.
    const seen = new Set<number>();
    let best: number[] = [];
    for (const s of members) {
      if (seen.has(s)) continue;
      const stack = [s];
      seen.add(s);
      const patch: number[] = [];
      while (stack.length) {
        const u = stack.pop()!;
        patch.push(u);
        for (const v of adj[u])
          if (!seen.has(v) && labels[v] === cls) {
            seen.add(v);
            stack.push(v);
          }
      }
      if (patch.length > best.length) best = patch;
    }
    const patchSet = new Set(best);
    const depth = new Map<number, number>();
    const bfs: number[] = [];
    for (const i of best) {
      if (
        adj[i].length < interiorDegree ||
        adj[i].some((v) => !patchSet.has(v))
      ) {
        depth.set(i, 0);
        bfs.push(i);
      }
    }
    for (let h = 0; h < bfs.length; h++) {
      const u = bfs[h];
      const du = depth.get(u)!;
      for (const v of adj[u])
        if (patchSet.has(v) && !depth.has(v)) {
          depth.set(v, du + 1);
          bfs.push(v);
        }
    }
    const mLng = best.reduce((s, i) => s + centroids[i].lng, 0) / best.length;
    const mLat = best.reduce((s, i) => s + centroids[i].lat, 0) / best.length;
    let anchor = best[0];
    let bestScore = -Infinity;
    for (const i of best) {
      const dep = depth.get(i) ?? 0;
      const d2 =
        (centroids[i].lng - mLng) ** 2 + (centroids[i].lat - mLat) ** 2;
      const score = dep * 1e9 - d2;
      if (score > bestScore) {
        bestScore = score;
        anchor = i;
      }
    }
    const code = codeOf(cls);
    features.push({
      type: "Feature",
      properties: {
        zone: cls,
        code,
        label: labelOf(cls),
        area_acres: Math.round(area * 100) / 100,
        mean_moisture: meanOf(drivers.moisture, members),
        mean_productivity: meanOf(drivers.productivity, members),
        mean_fertility: meanOf(drivers.fertility, members),
      },
      geometry: geom,
    });
    labelPoints.push({
      zone: cls,
      code,
      lng: centroids[anchor].lng,
      lat: centroids[anchor].lat,
    });
  }
  return { fc: { type: "FeatureCollection", features }, labelPoints };
}

// The shared dissolve is parameterized so the editor and the decile path cannot drift structurally.

/*
 *
 * Percentile → decile digit 0..9. Bins are half-open [d·10, d·10+10), with the TOP CLOSED: exactly 100
 * belongs to decile 9, not a phantom decile 10 — the clamp exists for that single boundary value.
 */
export function decileOf(pct: number | null | undefined): number | null {
  if (pct == null || !Number.isFinite(pct)) return null;
  return Math.min(9, Math.max(0, Math.floor(pct / 10)));
}
/* * Class index 0..999 — literally the code read as a number, so the label machinery stays integer-based. */
export function decileClassIndex(
  m: number | null,
  p: number | null,
  f: number | null,
): number | null {
  return m == null || p == null || f == null ? null : m * 100 + p * 10 + f;
}
/*
 *
 * The zone name: three digits, ZERO-PADDED — "058" is a real code (moisture 0-10th) and truncating it to
 * "58" would silently rename it as moisture-50s. Codes are strings for exactly this reason.
 */
export function decileCode(idx: number): string {
  return String(idx).padStart(3, "0");
}
const DIM_NAMES: [string, string, string] = [
  "Moisture",
  "Productivity",
  "Fertility",
];
export function decileLabel(idx: number): string {
  const d = [Math.floor(idx / 100), Math.floor((idx % 100) / 10), idx % 10];
  return d
    .map((v, i) => `${DIM_NAMES[i]} ${v * 10}\u2013${v * 10 + 10}th pctl`)
    .join(", ");
}

/* * The decile classifier over already-rescaled (0-100 percentile) drivers. */
function decileLabels(
  moisture: (number | null)[],
  productivity: (number | null)[],
  fertility: (number | null)[],
): { labels: number[]; classified: number } {
  const n = moisture.length;
  const labels = new Array<number>(n);
  let classified = 0;
  for (let i = 0; i < n; i++) {
    const idx = decileClassIndex(
      decileOf(moisture[i]),
      decileOf(productivity[i]),
      decileOf(fertility[i]),
    );
    labels[i] = idx == null ? -1 : idx;
    if (idx != null) classified++;
  }
  return { labels, classified };
}

/* * Redissolve for the editor — same dissolve as the 27-class path, decile naming. */
export function decileZonesFromLabels(
  geoms: { type: string; coordinates: unknown }[],
  labels: number[],
  drivers: BehaviorDrivers,
  opts?: { geom?: ZoneGeom },
): {
  fc: GeoJSON.FeatureCollection;
  labelPoints: { zone: number; code: string; lng: number; lat: number }[];
} {
  return behaviorZonesFromLabels(geoms, labels, drivers, {
    ...opts,
    codeOf: decileCode,
    labelOf: decileLabel,
  });
}

export function designDecileZones(a: {
  geoms: { type: string; coordinates: unknown }[];
  moisture: (number | null)[];
  productivity: (number | null)[];
  fertility: (number | null)[];
}): BehaviorZoneResult {
  const n = a.geoms.length;
  if (n < 20)
    throw new Error(
      "Not enough field data to zone (the covariate stack looks empty).",
    );
  const { labels, classified } = decileLabels(
    a.moisture,
    a.productivity,
    a.fertility,
  );
  if (classified < 20) {
    throw new Error(
      "The moisture / productivity / fertility layers have no values on this field yet — build them (run the covariate build / Map Soil) first.",
    );
  }
  const geom = buildZoneGeom(a.geoms);
  // No pocket absorption: every decile combination present in the data survives,
  // down to a single cell.
  const drivers: BehaviorDrivers = {
    moisture: a.moisture,
    productivity: a.productivity,
    fertility: a.fertility,
  };
  const { fc, labelPoints } = decileZonesFromLabels(a.geoms, labels, drivers, {
    geom,
  });
  return { fc, zoneCount: fc.features.length, classified, labelPoints, labels };
}

// The unique-code invariant step 3 protects holds by construction; `settleCollisions` stays as a
// guard (and the UI keeps its undershoot line) so that if the aggregation ever changes — median
// instead of mean, a different distance — the invariant is enforced rather than silently broken.

interface RZone {
  sums: [number, number, number];
  count: number;
  cells: number[];
  dec: [number, number, number];
}
const decOfZone = (z: {
  sums: [number, number, number];
  count: number;
}): [number, number, number] => [
  decileOf(z.sums[0] / z.count)!,
  decileOf(z.sums[1] / z.count)!,
  decileOf(z.sums[2] / z.count)!,
];
const codeOfDec = (d: [number, number, number]): number =>
  d[0] * 100 + d[1] * 10 + d[2];

export function reduceDecileZones(
  labels: number[],
  drivers: BehaviorDrivers,
  target: number,
): number[] {
  // build zone accounting from the FULL-resolution labels; -1 (unclassified) cells stay out entirely
  const byLabel = new Map<number, RZone>();
  for (let i = 0; i < labels.length; i++) {
    const l = labels[i]!;
    if (l < 0) continue;
    const m = drivers.moisture[i],
      p = drivers.productivity[i],
      f = drivers.fertility[i];
    if (m == null || p == null || f == null) continue;
    let z = byLabel.get(l);
    if (!z) {
      z = { sums: [0, 0, 0], count: 0, cells: [], dec: [0, 0, 0] };
      byLabel.set(l, z);
    }
    z.sums[0] += m;
    z.sums[1] += p;
    z.sums[2] += f;
    z.count++;
    z.cells.push(i);
  }
  const zones: RZone[] = [...byLabel.values()];
  for (const z of zones) z.dec = decOfZone(z);

  const t = Math.max(1, Math.floor(target));
  const dist2 = (a: [number, number, number], b: [number, number, number]) =>
    (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2;

  const mergeInto = (dst: RZone, src: RZone) => {
    dst.sums[0] += src.sums[0];
    dst.sums[1] += src.sums[1];
    dst.sums[2] += src.sums[2];
    dst.count += src.count;
    dst.cells.push(...src.cells);
    zones.splice(zones.indexOf(src), 1);
  };
  // step 3's invariant, to a FIXPOINT: recompute dst's deciles; while any OTHER zone shares them, merge it
  // in too — which changes the means, so recompute and look again
  const settleCollisions = (dst: RZone) => {
    for (;;) {
      dst.dec = decOfZone(dst);
      const twin = zones.find(
        (z) =>
          z !== dst &&
          z.dec[0] === dst.dec[0] &&
          z.dec[1] === dst.dec[1] &&
          z.dec[2] === dst.dec[2],
      );
      if (!twin) return;
      mergeInto(dst, twin);
    }
  };

  while (zones.length > t) {
    // (1) the smallest zone… (ties: lower code, for determinism)
    let src = zones[0]!;
    for (const z of zones) {
      if (
        z.count < src.count ||
        (z.count === src.count && codeOfDec(z.dec) < codeOfDec(src.dec))
      )
        src = z;
    }
    // …merged into the zone with the nearest decile numbers (ties: more cells, then lower code)
    let dst: RZone | null = null;
    let best = Infinity;
    for (const z of zones) {
      if (z === src) continue;
      const d = dist2(src.dec, z.dec);
      if (
        d < best ||
        (d === best &&
          dst != null &&
          (z.count > dst.count ||
            (z.count === dst.count && codeOfDec(z.dec) < codeOfDec(dst.dec))))
      ) {
        best = d;
        dst = z;
      }
    }
    if (!dst) break; // one zone left
    mergeInto(dst, src);
    // (2) + (3)
    settleCollisions(dst);
    // (4): the while condition
  }

  // relabel every cell with its zone's FINAL decile code (unique by the invariant)
  const out = labels.slice();
  for (const z of zones) {
    const code = codeOfDec(z.dec);
    for (const i of z.cells) out[i] = code;
  }
  return out;
}

// PATCH-level, not class-level — the opposite axis from the reducer: a class keeps its identity and
// its other patches; only the sub-floor fragment moves.
export const SMOOTH_MIN_AREA_M2 = 650;

const digitsOf = (code: number): [number, number, number] => [
  Math.floor(code / 100),
  Math.floor((code % 100) / 10),
  code % 10,
];

export function smoothDecilePatches(
  labels: number[],
  adj: number[][],
  cellAreaM2: number,
  minAreaM2 = SMOOTH_MIN_AREA_M2,
): number[] {
  const n = labels.length;
  const cur = labels.slice();
  for (let guard = 0; guard <= n; guard++) {
    // connected components of same label (gaps < 0 are never members)
    const comp = new Array<number>(n).fill(-1);
    const members: number[][] = [];
    const compLabel: number[] = [];
    for (let i = 0; i < n; i++) {
      if (comp[i] !== -1 || cur[i]! < 0) continue;
      const id = members.length;
      comp[i] = id;
      const stack = [i];
      const mem: number[] = [];
      while (stack.length) {
        const u = stack.pop()!;
        mem.push(u);
        for (const v of adj[u]!)
          if (comp[v] === -1 && cur[v] === cur[i]) {
            comp[v] = id;
            stack.push(v);
          }
      }
      members.push(mem);
      compLabel.push(cur[i]!);
    }
    // The first pass selected only the single smallest and `continue`d when it was gap-locked
    // which re-selected the SAME island every iteration: the loop spun to its guard bound and the
    // OTHER sub-floor patches were never smoothed (compute caught this porting the goldens the
    // queue behind it.
    const order: number[] = [];
    for (let c = 0; c < members.length; c++)
      if (members[c]!.length * cellAreaM2 < minAreaM2) order.push(c);
    order.sort(
      (a, b) =>
        members[a]!.length - members[b]!.length ||
        compLabel[a]! - compLabel[b]!,
    );
    let merged = false;
    for (const target of order) {
      // among ADJACENT patches: nearest decile digits, then larger, then lower code
      const d0 = digitsOf(compLabel[target]!);
      let best = -1;
      let bestD = Infinity;
      for (const u of members[target]!)
        for (const v of adj[u]!) {
          const nc = comp[v]!;
          if (nc === target || nc < 0 || compLabel[nc]! < 0) continue;
          const dg = digitsOf(compLabel[nc]!);
          const d =
            (dg[0] - d0[0]) ** 2 + (dg[1] - d0[1]) ** 2 + (dg[2] - d0[2]) ** 2;
          if (
            d < bestD ||
            (d === bestD &&
              best !== -1 &&
              (members[nc]!.length > members[best]!.length ||
                (members[nc]!.length === members[best]!.length &&
                  compLabel[nc]! < compLabel[best]!)))
          ) {
            bestD = d;
            best = nc;
          }
        }
      if (best === -1) continue; // gap-locked island: survives, and the NEXT candidate gets its turn
      const newLabel = compLabel[best]!;
      for (const u of members[target]!) cur[u] = newLabel;
      merged = true;
      break; // labels changed — components must be rebuilt before touching anything else
    }
    if (!merged) break;
  }
  return cur;
}

/*
 *
 * THE default zone-count target: **boundary acres ÷ 22, ALWAYS ROUNDED UP** was acres÷8
 * round-half-up; ceil also retires the half-up-vs-banker's cross-language rounding trap compute had
 * to guard with `_js_round`). Null when it would not actually reduce (≥ the distinct combination
 * count): the stepper expresses "no reduction" as null, and a target equal to the max is the same
 * thing wearing a number.
 */
export function defaultZoneTarget(
  areaAcres: number,
  distinct: number,
): number | null {
  const def = Math.max(1, Math.ceil(areaAcres / 22));
  return def < distinct ? def : null;
}

/*
 *
 * THE default decile map, end to end: classify → acres÷22 target → reduce → smooth.
 */
export function defaultDecileChain(a: {
  geoms: { type: string; coordinates: unknown }[];
  moisture: (number | null)[];
  productivity: (number | null)[];
  fertility: (number | null)[];
  /*
   *
   * The sum remains only a fallback for callers with no field row in hand.
   */
  boundaryAcres?: number;
}): BehaviorZoneResult {
  const full = designDecileZones(a);
  const areaAcres =
    a.boundaryAcres ??
    a.geoms.reduce((s, g) => s + polygonAreaM2(g as never) / 4046.86, 0);
  const distinct = new Set(full.labels.filter((l) => l >= 0)).size;
  const target = defaultZoneTarget(areaAcres, distinct);
  const drivers: BehaviorDrivers = {
    moisture: a.moisture,
    productivity: a.productivity,
    fertility: a.fertility,
  };
  const reduced =
    target != null
      ? reduceDecileZones(full.labels, drivers, target)
      : full.labels;
  const geom = buildZoneGeom(a.geoms);
  const first = geom.ringsPerCell[0]?.[0];
  const cellArea = first
    ? polygonAreaM2({ type: "Polygon", coordinates: [[...first, first[0]]] })
    : 64;
  const smoothed = smoothDecilePatches(reduced, geom.adj, cellArea);
  const { fc, labelPoints } = decileZonesFromLabels(
    a.geoms,
    smoothed,
    drivers,
    { geom },
  );
  return {
    fc,
    zoneCount: fc.features.length,
    classified: full.classified,
    labelPoints,
    labels: smoothed,
  };
}
